import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Send, Check } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in name, email, and message.');
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      toast.success("Thanks! We'll be in touch shortly.");
    }, 800);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/60 to-white">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 md:pt-24 md:pb-16">
          <div className="max-w-3xl">
            <span className="inline-block text-[12px] font-bold tracking-[0.18em] uppercase text-blue-600 mb-6">Contact Us</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]" style={{ fontFamily: 'Manrope' }}>
              Get in touch.
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Questions about Zap AI or our products? Want a personalised demo? Send us a message and we will get back to you, usually within one business day.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
                Reach us directly
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                Prefer email? We read every message that comes in.
              </p>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Email</p>
                    <a href="mailto:info@zapai.com.au" className="text-sm text-blue-600 hover:underline">info@zapai.com.au</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Send className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Response time</p>
                    <p className="text-sm text-slate-500">Within one business day, Mon to Fri.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-slate-200/60 bg-slate-50/40 p-8">
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-5">
                      <Check className="h-7 w-7 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Thanks for reaching out</h3>
                    <p className="text-base text-slate-600">We have received your message and will reply soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-1.5">
                      <Label htmlFor="contact-name">Name <span className="text-red-500">*</span></Label>
                      <Input id="contact-name" name="name" placeholder="Your full name" value={form.name} onChange={handleChange} />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="contact-email">Email <span className="text-red-500">*</span></Label>
                        <Input id="contact-email" name="email" type="email" placeholder="you@business.com" value={form.email} onChange={handleChange} />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="contact-phone">Phone</Label>
                        <Input id="contact-phone" name="phone" type="tel" placeholder="+61 4XX XXX XXX" value={form.phone} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="contact-message">Message <span className="text-red-500">*</span></Label>
                      <Textarea id="contact-message" name="message" rows={5} placeholder="Tell us a bit about your business and what you are looking for..." value={form.message} onChange={handleChange} />
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11 font-medium">
                      {submitting ? 'Sending...' : 'Send Message'} {!submitting && <Send className="ml-2 h-4 w-4" />}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
